import { useId, useState } from "react";
import { ApiError, uploadFileWithAuth } from "../../api/client";

type Props = {
  label: string;
  /** Shown under the file control. */
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  /** Passed to the API as `form_identifier` for debugging. */
  formIdentifier: string;
  maxUrlLength?: number;
};

/**
 * Image URL field with optional file upload (fills URL with public `/uploads/...` path).
 * Uses nested <label> elements so `.admin-blog-form-fields label` styles apply on blog/event forms.
 */
export function AdminImageUploadField({
  label,
  hint = "Upload replaces the URL below. You can still paste an external image URL if you prefer.",
  value,
  onChange,
  disabled = false,
  formIdentifier,
  maxUrlLength = 500,
}: Props) {
  const id = useId();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || disabled) return;
    setUploading(true);
    setUploadError(null);
    try {
      const data = await uploadFileWithAuth(file, { formIdentifier });
      if (!data.mimetype.startsWith("image/")) {
        setUploadError("Please choose an image file.");
        return;
      }
      onChange(data.uploadsUrl);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="admin-image-upload-field">
      <label htmlFor={`${id}-file`}>
        <span>{label}</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <input
            id={`${id}-file`}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            onChange={(ev) => void onPickFile(ev)}
            disabled={disabled || uploading}
          />
          {uploading && <span style={{ opacity: 0.8, textTransform: "none", letterSpacing: "normal" }}>Uploading…</span>}
        </div>
      </label>
      {hint && (
        <p style={{ margin: "4px 0 0", opacity: 0.75, fontSize: "0.9rem", textTransform: "none", letterSpacing: "normal", fontFamily: "var(--sans)" }}>
          {hint}
        </p>
      )}
      {uploadError && <div className="form-error" style={{ marginTop: 8 }}>{uploadError}</div>}
      <label htmlFor={`${id}-url`} style={{ marginTop: 12 }}>
        <span>URL or path</span>
        <input
          id={`${id}-url`}
          type="text"
          maxLength={maxUrlLength}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="/uploads/… or https://…"
        />
      </label>
      {value && /^https?:\/\//i.test(value) === false && (
        <p style={{ marginTop: 8, fontSize: "0.85rem", opacity: 0.75, textTransform: "none", letterSpacing: "normal", fontFamily: "var(--sans)" }}>
          Paths like <code>/uploads/…</code> are served from this site (no login required for visitors).
        </p>
      )}
    </div>
  );
}
